import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BrainOrb, Button, Card, GlassPill } from '../components/primitives';
import { useActiveTeam } from '../lib/queries/useActiveTeam';
import { useTeamGames, type GameRow } from '../lib/queries/useTeamGames';
import { supabase, isPlaceholderSupabase } from '../lib/supabase';
import './page-shell.css';
import './Games.css';

function isUpcoming(g: GameRow): boolean {
  // Treat anything from today onwards as upcoming (date is YYYY-MM-DD).
  const today = new Date().toISOString().slice(0, 10);
  return g.date >= today;
}

function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

export function Games() {
  const { team, loading: teamLoading } = useActiveTeam();
  const { rows, state, errorMessage, refresh } = useTeamGames(team?.id, teamLoading);

  const [showForm, setShowForm] = useState(false);
  const [opponent, setOpponent] = useState('');
  const [date, setDate] = useState('');
  const [homeAway, setHomeAway] = useState<'HOME' | 'AWAY' | 'NEUTRAL'>('HOME');
  const [competition, setCompetition] = useState<'TOURNAMENT' | 'LEAGUE' | 'CUP' | 'FRIENDLY'>('LEAGUE');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const { upcoming, past } = useMemo(() => {
    const upcoming: GameRow[] = [];
    const past: GameRow[] = [];
    for (const g of rows) {
      if (isUpcoming(g)) upcoming.push(g);
      else past.push(g);
    }
    // Upcoming oldest-first (next game on top); past newest-first (already from DB).
    upcoming.sort((a, b) => a.date.localeCompare(b.date));
    return { upcoming, past };
  }, [rows]);

  const nextGame = upcoming[0] ?? null;

  const handleSchedule = async () => {
    if (!team) return;
    if (!opponent.trim() || !date) {
      setSaveError('Opponent and date are both required.');
      return;
    }
    if (isPlaceholderSupabase()) {
      setSaveError('Connect a Supabase backend to schedule games.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    const { error } = await supabase.from('games').insert({
      team_id: team.id,
      opponent: opponent.trim(),
      date,
      home_away: homeAway,
      competition,
    });
    setSaving(false);
    if (error) {
      setSaveError(error.message);
      return;
    }
    setOpponent('');
    setDate('');
    setHomeAway('HOME');
    setCompetition('LEAGUE');
    setShowForm(false);
    refresh();
  };

  return (
    <main className="main page games-page">
      <header className="page-head">
        <BrainOrb size="lg" ariaLabel="Brain" />
        <div>
          <span className="page-eyebrow">Layer 2 · match-day</span>
          <h1 className="page-title">Games</h1>
          <p className="page-sub">Schedule fixtures, run game-day, log the report.</p>
        </div>
      </header>

      {state === 'loading' && <p className="page-empty">Loading…</p>}
      {state === 'error' && (
        <Card variant="opaque">
          <p className="page-empty">
            Couldn&apos;t load games.{errorMessage ? ` (${errorMessage})` : null}
          </p>
        </Card>
      )}

      {nextGame && (
        <Card variant="opaque">
          <div className="games-next">
            <span className="page-eyebrow">Next up</span>
            <h2 className="games-next__title">
              vs {nextGame.opponent} <span className="games-next__date">· {formatDate(nextGame.date)}</span>
            </h2>
            <div className="games-next__pills">
              <GlassPill mono>{nextGame.home_away}</GlassPill>
              {nextGame.competition && <GlassPill mono>{nextGame.competition}</GlassPill>}
              {nextGame.venue && <GlassPill mono>{nextGame.venue}</GlassPill>}
            </div>
            <div className="games-next__actions">
              <Link to={`/games/${nextGame.id}`} className="games-next__cta">
                Open game-day →
              </Link>
            </div>
          </div>
        </Card>
      )}

      <section className="games-list">
        <div className="games-list__head">
          <span className="page-eyebrow">Upcoming · {upcoming.length}</span>
          <Button variant="primary" size="touchline" onClick={() => setShowForm((v) => !v)}>
            {showForm ? 'Cancel' : '+ Schedule game'}
          </Button>
        </div>

        {showForm && (
          <Card variant="opaque">
            <div className="games-form">
              <label className="games-form__field">
                <span>Opponent</span>
                <input
                  type="text"
                  value={opponent}
                  onChange={(e) => setOpponent(e.target.value)}
                  placeholder="e.g. Aurora Lions U12"
                  autoFocus
                />
              </label>
              <label className="games-form__field">
                <span>Date</span>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </label>
              <label className="games-form__field">
                <span>Home / Away</span>
                <select value={homeAway} onChange={(e) => setHomeAway(e.target.value as 'HOME' | 'AWAY' | 'NEUTRAL')}>
                  <option value="HOME">Home</option>
                  <option value="AWAY">Away</option>
                  <option value="NEUTRAL">Neutral</option>
                </select>
              </label>
              <label className="games-form__field">
                <span>Competition</span>
                <select value={competition} onChange={(e) => setCompetition(e.target.value as typeof competition)}>
                  <option value="LEAGUE">League</option>
                  <option value="CUP">Cup</option>
                  <option value="TOURNAMENT">Tournament</option>
                  <option value="FRIENDLY">Friendly</option>
                </select>
              </label>
              {saveError && <p className="games-form__error">{saveError}</p>}
              <div className="games-form__actions">
                <Button variant="primary" size="touchline" onClick={() => void handleSchedule()} disabled={saving}>
                  {saving ? 'Saving…' : 'Schedule'}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {upcoming.length === 0 && state !== 'loading' && (
          <Card variant="opaque">
            <p className="page-empty">No upcoming games. Schedule one — even a friendly counts.</p>
          </Card>
        )}

        <ul className="games-grid">
          {upcoming.map((g) => (
            <li key={g.id}>
              <Link to={`/games/${g.id}`}>
                <Card variant="opaque">
                  <div className="games-card">
                    <div className="games-card__date">{formatDate(g.date)}</div>
                    <div className="games-card__opponent">vs {g.opponent}</div>
                    <div className="games-card__pills">
                      <GlassPill mono>{g.home_away}</GlassPill>
                      {g.competition && <GlassPill mono>{g.competition}</GlassPill>}
                    </div>
                  </div>
                </Card>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {past.length > 0 && (
        <section className="games-list">
          <div className="games-list__head">
            <span className="page-eyebrow">Recent · {past.length}</span>
          </div>
          <ul className="games-grid">
            {past.slice(0, 12).map((g) => (
              <li key={g.id}>
                <Link to={`/games/${g.id}`}>
                  <Card variant="opaque">
                    <div className="games-card">
                      <div className="games-card__date">{formatDate(g.date)}</div>
                      <div className="games-card__opponent">vs {g.opponent}</div>
                      <div className="games-card__pills">
                        {g.result && <GlassPill mono>{g.result}</GlassPill>}
                        <GlassPill mono>{g.home_away}</GlassPill>
                      </div>
                    </div>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}
